import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import {
  Clock,
  ArrowUpDown,
  ChevronDown,
  User,
  Check,
  Star,
  FileText,
  CheckCircle2,
  Video,
  Mail,
  X,
  Info,
  Plus,
} from 'lucide-react';
import { StatusHistoryItem } from '../../../utils/types/JobOpenings';

const MotionBox = motion(Box);

export interface StatusHistoryTimelineProps {
  statusHistory?: StatusHistoryItem[];
  currentStatus?: string;
  createdAt?: number | string;
  className?: string;
  title?: string;
  collapsible?: boolean;
}

// Helpers for timestamp parsing and formatting
const parseTimestampToDate = (ts: number | string | undefined | null): Date | null => {
  if (!ts) return null;
  if (typeof ts === 'number') {
    return new Date(ts < 100000000000 ? ts * 1000 : ts);
  }
  const num = Number(ts);
  if (!isNaN(num)) {
    return new Date(num < 100000000000 ? num * 1000 : num);
  }
  const parsed = new Date(ts);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatFullDate = (date: Date | null): string => {
  if (!date) return 'Date unknown';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatRelativeTime = (date: Date | null): string => {
  if (!date) return '';
  const now = Date.now();
  const diffSec = Math.floor((now - date.getTime()) / 1000);

  if (diffSec < 0 || diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  if (diffSec < 2592000) return `${Math.floor(diffSec / 604800)}w ago`;
  return `${Math.floor(diffSec / 2592000)}mo ago`;
};

// Configuration for status colors, icons, and themes
interface StatusConfig {
  label: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  dotBg: string;
  ringColor: string;
  icon: React.ReactNode;
}

const getStatusConfig = (statusRaw: string): StatusConfig => {
  const status = (statusRaw || '').trim();

  switch (status) {
    case 'New':
      return {
        label: 'New Application',
        bgColor: '#FFFBEB',
        borderColor: '#FDE68A',
        textColor: '#92400E',
        dotBg: '#F59E0B',
        ringColor: 'rgba(245, 158, 11, 0.4)',
        icon: <Plus size={14} strokeWidth={2.5} />,
      };
    case 'Suitable':
      return {
        label: 'Suitable',
        bgColor: '#F0FDFA',
        borderColor: '#99F6E4',
        textColor: '#115E59',
        dotBg: '#14B8A6',
        ringColor: 'rgba(20, 184, 166, 0.4)',
        icon: <Check size={14} strokeWidth={2.5} />,
      };
    case 'Outstanding':
      return {
        label: 'Outstanding',
        bgColor: '#EFF6FF',
        borderColor: '#BFDBFE',
        textColor: '#1E40AF',
        dotBg: '#3B82F6',
        ringColor: 'rgba(59, 130, 246, 0.4)',
        icon: <Star size={14} strokeWidth={2.5} />,
      };
    case 'Auto-Assessment':
      return {
        label: 'Auto-Assessment',
        bgColor: '#FAF5FF',
        borderColor: '#E9D5FF',
        textColor: '#6B21A8',
        dotBg: '#A855F7',
        ringColor: 'rgba(168, 85, 247, 0.4)',
        icon: <FileText size={14} strokeWidth={2.5} />,
      };
    case 'Shortlisted':
      return {
        label: 'Shortlisted',
        bgColor: '#ECFDF5',
        borderColor: '#A7F3D0',
        textColor: '#065F46',
        dotBg: '#10B981',
        ringColor: 'rgba(16, 185, 129, 0.4)',
        icon: <CheckCircle2 size={14} strokeWidth={2.5} />,
      };
    case 'Interview':
    case 'Scheduled':
      return {
        label: 'Interview',
        bgColor: '#EEF2FF',
        borderColor: '#C7D2FE',
        textColor: '#3730A3',
        dotBg: '#6366F1',
        ringColor: 'rgba(99, 102, 241, 0.4)',
        icon: <Video size={14} strokeWidth={2.5} />,
      };
    case 'Offer':
      return {
        label: 'Job Offer',
        bgColor: '#ECFEFF',
        borderColor: '#A5F3FC',
        textColor: '#155E75',
        dotBg: '#06B6D4',
        ringColor: 'rgba(6, 182, 212, 0.4)',
        icon: <Mail size={14} strokeWidth={2.5} />,
      };
    case 'Completed':
      return {
        label: 'Completed',
        bgColor: '#F0FDF4',
        borderColor: '#BBF7D0',
        textColor: '#166534',
        dotBg: '#22C55E',
        ringColor: 'rgba(34, 197, 94, 0.4)',
        icon: <Check size={14} strokeWidth={2.5} />,
      };
    case 'Unsuccessful':
    case 'Unsuccessful-Closed':
    case 'Unsuccessful-Pending':
      return {
        label: 'Unsuccessful',
        bgColor: '#FFF1F2',
        borderColor: '#FECDD3',
        textColor: '#9F1239',
        dotBg: '#F43F5E',
        ringColor: 'rgba(244, 63, 94, 0.4)',
        icon: <X size={14} strokeWidth={2.5} />,
      };
    case 'Waitlisted':
      return {
        label: 'Waitlisted',
        bgColor: '#F1F5F9',
        borderColor: '#CBD5E1',
        textColor: '#334155',
        dotBg: '#64748B',
        ringColor: 'rgba(100, 116, 139, 0.4)',
        icon: <Clock size={14} strokeWidth={2.5} />,
      };
    default:
      return {
        label: status || 'Updated',
        bgColor: '#F8FAFC',
        borderColor: '#E2E8F0',
        textColor: '#334155',
        dotBg: '#94A3B8',
        ringColor: 'rgba(148, 163, 184, 0.4)',
        icon: <Info size={14} strokeWidth={2.5} />,
      };
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 28,
    },
  },
};

export const StatusHistoryTimeline: React.FC<StatusHistoryTimelineProps> = ({
  statusHistory = [],
  currentStatus,
  createdAt,
  title = 'Application Timeline',
  collapsible = false,
}) => {
  const [isNewestFirst, setIsNewestFirst] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  // Normalize history list
  const historyItems: StatusHistoryItem[] = React.useMemo(() => {
    let items = Array.isArray(statusHistory) ? [...statusHistory] : [];

    // If history is empty but currentStatus is known, synthesize an entry
    if (items.length === 0 && currentStatus && currentStatus !== 'N/A') {
      items.push({
        status: currentStatus,
        timestamp: createdAt || Date.now(),
        actorId: null,
      });
    }

    // Sort items by parsed timestamp
    items.sort((a, b) => {
      const dateA = parseTimestampToDate(a.timestamp)?.getTime() || 0;
      const dateB = parseTimestampToDate(b.timestamp)?.getTime() || 0;
      return isNewestFirst ? dateB - dateA : dateA - dateB;
    });

    return items;
  }, [statusHistory, currentStatus, createdAt, isNewestFirst]);

  if (historyItems.length === 0 && (!currentStatus || currentStatus === 'N/A')) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #F1F5F9',
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          background: 'linear-gradient(to right, #ECFDF5, #F0FDFA, #FFFFFF)',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #F1F5F9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: 'rgba(5, 150, 105, 0.1)',
              color: '#047857',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Clock size={16} />
          </Box>
          <Box>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography variant='subtitle2' sx={{ fontWeight: 700, color: '#111827' }}>
                {title}
              </Typography>
              <Chip
                label={`${historyItems.length} ${historyItems.length === 1 ? 'stage' : 'stages'}`}
                size='small'
                sx={{
                  height: 20,
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  color: '#047857',
                  bgcolor: '#D1FAE5',
                  borderRadius: 10,
                }}
              />
            </Stack>
            {currentStatus && (
              <Typography variant='caption' sx={{ color: '#6B7280', fontWeight: 500 }}>
                Current Stage:{' '}
                <Box component='span' sx={{ fontWeight: 700, color: '#047857' }}>
                  {currentStatus}
                </Box>
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Action Controls: Sort toggle + Optional Collapse */}
        <Stack direction='row' alignItems='center' spacing={1}>
          {historyItems.length > 1 && (
            <Button
              size='small'
              onClick={() => setIsNewestFirst(!isNewestFirst)}
              startIcon={<ArrowUpDown size={12} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                color: '#4B5563',
                py: 0.5,
                px: 1.5,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#F9FAFB',
                  borderColor: '#A7F3D0',
                  color: '#047857',
                },
              }}
            >
              {isNewestFirst ? 'Newest' : 'Chronological'}
            </Button>
          )}

          {collapsible && (
            <IconButton
              size='small'
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{
                color: '#9CA3AF',
                '&:hover': { bgcolor: '#F3F4F6', color: '#4B5563' },
              }}
              aria-label={isExpanded ? 'Collapse timeline' : 'Expand timeline'}
            >
              <ChevronDown
                size={16}
                style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
              />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Animated Timeline List */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <MotionBox
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            sx={{ overflow: 'hidden' }}
          >
            <MotionBox
              variants={containerVariants}
              initial='hidden'
              animate='visible'
              sx={{ p: { xs: 2, sm: 2.5 }, position: 'relative' }}
            >
              {/* Vertical connecting rail */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 32, sm: 36 },
                  top: 28,
                  bottom: 28,
                  width: 2,
                  background: 'linear-gradient(to bottom, #10B981, #2DD4BF, #E2E8F0)',
                  zIndex: 0,
                }}
              />

              <Stack spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                {historyItems.map((item, index) => {
                  const dateObj = parseTimestampToDate(item.timestamp);
                  const isLatest = index === 0;
                  const config = getStatusConfig(item.status);

                  return (
                    <MotionBox
                      key={`${item.status}-${item.timestamp}-${index}`}
                      variants={itemVariants}
                      whileHover={{ x: 2, transition: { duration: 0.15 } }}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75 }}
                    >
                      {/* Node Icon on Timeline */}
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        {isLatest && (
                          <MotionBox
                            sx={{
                              position: 'absolute',
                              inset: -4,
                              borderRadius: '50%',
                              backgroundColor: config.ringColor,
                            }}
                            animate={{
                              scale: [1, 1.45, 1],
                              opacity: [0.7, 0, 0.7],
                            }}
                            transition={{
                              duration: 2.2,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFFFFF',
                            bgcolor: config.dotBg,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            border: '2px solid #FFFFFF',
                          }}
                        >
                          {config.icon}
                        </Box>
                      </Box>

                      {/* Event Card */}
                      <Box
                        sx={{
                          flex: 1,
                          borderRadius: 2.5,
                          p: { xs: 1.5, sm: 1.75 },
                          border: `1px solid ${config.borderColor}`,
                          bgcolor: config.bgColor,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          },
                        }}
                      >
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          flexWrap='wrap'
                          gap={1}
                        >
                          {/* Status Badge & Label */}
                          <Stack direction='row' alignItems='center' spacing={1}>
                            <Typography
                              variant='caption'
                              sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1.5,
                                border: `1px solid ${config.borderColor}`,
                                color: config.textColor,
                                bgcolor: 'rgba(255, 255, 255, 0.85)',
                              }}
                            >
                              {item.status}
                            </Typography>
                            {isLatest && (
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={0.5}
                                sx={{
                                  fontSize: '0.625rem',
                                  fontWeight: 800,
                                  color: '#047857',
                                  bgcolor: '#D1FAE5',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 10,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: '#10B981',
                                  }}
                                />
                                <Box component='span'>Current</Box>
                              </Stack>
                            )}
                          </Stack>

                          {/* Relative & Full Time Pill */}
                          {dateObj && (
                            <Stack direction='row' alignItems='center' spacing={1}>
                              <Typography
                                variant='caption'
                                sx={{ color: '#6B7280', fontWeight: 500 }}
                              >
                                {formatFullDate(dateObj)}
                              </Typography>
                              <Box
                                component='span'
                                sx={{
                                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                                  border: '1px solid rgba(229, 231, 235, 0.8)',
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: '0.625rem',
                                  fontWeight: 600,
                                  color: '#4B5563',
                                }}
                              >
                                {formatRelativeTime(dateObj)}
                              </Box>
                            </Stack>
                          )}
                        </Stack>

                        {/* Actor / Trigger source */}
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <User size={12} color='#9CA3AF' />
                          <Typography
                            variant='caption'
                            sx={{ color: '#6B7280', fontWeight: 500 }}
                          >
                            {item.actorName
                              ? `Action by ${item.actorName}`
                              : item.actorId
                              ? `Actor: ${item.actorId}`
                              : 'System Pipeline Automation'}
                          </Typography>
                        </Box>

                        {/* Note / Reason if present */}
                        {(item.note || item.reason) && (
                          <Paper
                            elevation={0}
                            sx={{
                              mt: 1.5,
                              p: 1.25,
                              borderRadius: 2,
                              bgcolor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(241, 245, 249, 0.8)',
                            }}
                          >
                            <Typography
                              variant='caption'
                              sx={{
                                fontStyle: 'italic',
                                color: '#374151',
                                display: 'block',
                                lineHeight: 1.5,
                              }}
                            >
                              {item.note || item.reason}
                            </Typography>
                          </Paper>
                        )}
                      </Box>
                    </MotionBox>
                  );
                })}
              </Stack>
            </MotionBox>
          </MotionBox>
        )}
      </AnimatePresence>
    </Paper>
  );
};

export default StatusHistoryTimeline;
